'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn('fixed inset-0 z-50 bg-black/70 backdrop-blur-sm', className)}
      {...props}
    />
  );
}

const sheetContentVariants = cva(
  'fixed z-50 flex flex-col bg-[var(--color-neutral-950)] border-[var(--color-neutral-800)] shadow-lg',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b rounded-b-2xl max-h-[85vh]',
        bottom: 'inset-x-0 bottom-0 border-t rounded-t-2xl max-h-[85vh]',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
        right: 'inset-y-0 right-0 h-full w-3/4 border-r sm:max-w-sm',
      },
    },
    defaultVariants: {
      side: 'bottom',
    },
  }
);

// Close icon for sheet
function CloseIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M18 6 6 18M6 6l12 12"
      />
    </svg>
  );
}

interface SheetContentProps
  extends React.ComponentProps<typeof SheetPrimitive.Content>, VariantProps<typeof sheetContentVariants> {
  /** Whether to show the drag handle (useful for bottom/top sheets) */
  showHandle?: boolean;
  /** Whether to show the close button */
  showCloseButton?: boolean;
}

function SheetContent({
  side = 'bottom',
  showHandle = true,
  showCloseButton = true,
  className,
  children,
  ...props
}: SheetContentProps) {
  const isVertical = side === 'top' || side === 'bottom';

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-sheet-side={side}
        className={cn(sheetContentVariants({ side }), className)}
        {...props}
      >
        {/* Drag handle for vertical sheets */}
        {isVertical && showHandle && (
          <div className="flex justify-center py-3">
            <div className="h-1 w-10 rounded-full bg-[var(--color-neutral-600)]" />
          </div>
        )}

        {/* Close button */}
        {showCloseButton && (
          <SheetPrimitive.Close
            className="absolute top-4 right-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-[var(--color-neutral-900)] text-[var(--color-neutral-300)] transition-colors hover:bg-[var(--color-neutral-600)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-light)]"
            aria-label="Fechar"
          >
            <CloseIcon />
          </SheetPrimitive.Close>
        )}

        {/* Content wrapper */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Safe area bottom padding for bottom sheets */}
        {side === 'bottom' && <div className="h-6 shrink-0" />}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-header" className={cn('flex flex-col gap-1.5 px-4 pt-2 pb-4', className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-footer" className={cn('mt-auto flex flex-col gap-2 px-4 pb-4', className)} {...props} />;
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-base font-semibold text-[var(--color-neutral-100)]', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm leading-relaxed whitespace-pre-line text-[var(--color-neutral-300)]', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
